import {
	IconBook2,
	IconCalendar,
	IconCalendarEvent,
	IconDownload,
	IconUsersGroup,
	IconVideo,
} from "@tabler/icons-react"
import { type FC, useEffect, useMemo, useState } from "react"

import {
	type LandingPageProductListBlock,
	type ProductListBlockContent,
	type ProductListType,
} from "@forento/core/models/landing"
import { landingPageCopyCharacterLimits } from "@forento/core/schemas/landing"
import { cn } from "@forento/ui/utilities/tailwind"

import { query } from "~/utilities/trpc"

import EditableField from "./EditableField"
import EditorShell from "./EditorShell"
import ProductLinkTargetField from "./ProductLinkTargetField"

type Props = {
	block: LandingPageProductListBlock
	onChange: (block: LandingPageProductListBlock) => void
	onClose: () => void
}

const productTypeConfig: Record<
	ProductListType,
	{ icon: FC<{ className?: string }>; label: string; labelPlural: string }
> = {
	course: { icon: IconBook2, label: "Course", labelPlural: "courses" },
	video: { icon: IconVideo, label: "Video", labelPlural: "videos" },
	downloadable: { icon: IconDownload, label: "Downloadable", labelPlural: "downloadables" },
	session: { icon: IconCalendarEvent, label: "Session", labelPlural: "sessions" },
	event: { icon: IconCalendar, label: "Event", labelPlural: "events" },
	membership: { icon: IconUsersGroup, label: "Membership", labelPlural: "memberships" },
}

const ProductsBlockEditor: FC<Props> = ({ block, onChange, onClose }) => {
	const { data: options = [], isLoading } = query.landing.productHighlightOptions.useQuery(undefined, {
		refetchOnWindowFocus: false,
	})
	const [productType, setProductType] = useState<ProductListType>(block.content.productType)
	const [title, setTitle] = useState(block.content.title ?? "")
	const [productIds, setProductIds] = useState<number[] | undefined>(block.content.productIds)
	const [linkTarget, setLinkTarget] = useState(block.content.linkTarget ?? "product-page")

	useEffect(() => {
		const content: ProductListBlockContent = {
			productType,
			title: title.trim() || undefined,
			...(productIds !== undefined ? { productIds } : {}),
			linkTarget,
		}
		onChange({ type: "product-list", content })
	}, [linkTarget, onChange, productIds, productType, title])

	const productOptions = useMemo(() => options.filter(option => option.type === productType), [options, productType])
	const selectedProductIds = productIds ?? []
	const isShowingAllProducts = productIds === undefined

	const { icon: Icon, label, labelPlural } = productTypeConfig[productType]

	const handleProductTypeChange = (newType: ProductListType) => {
		setProductType(newType)
		setProductIds(undefined)
	}

	const toggleProductId = (productId: number) => {
		setProductIds(current => {
			const ids = current ?? []
			return ids.includes(productId) ? ids.filter(id => id !== productId) : [...ids, productId]
		})
	}

	return (
		<EditorShell onClose={onClose}>
			<section className="px-6 pt-16 pb-8 sm:pt-20 lg:px-8">
				<div className="mx-auto max-w-7xl">
					<div className="mb-8 rounded-2xl border border-indigo-100 bg-indigo-50/60 p-5">
						<h3 className="mb-3 text-sm font-semibold text-gray-900">Product type</h3>
						<div className="flex flex-wrap gap-2">
							{(
								Object.entries(productTypeConfig) as [
									ProductListType,
									(typeof productTypeConfig)[ProductListType],
								][]
							).map(([type, config]) => (
								<button
									key={type}
									type="button"
									onClick={() => handleProductTypeChange(type)}
									className={cn(
										"cursor-pointer rounded-lg border px-4 py-2 text-sm font-semibold transition",
										productType === type
											? "border-indigo-500 bg-indigo-500 text-white"
											: "border-gray-200 bg-white text-gray-700 hover:bg-gray-50",
									)}
								>
									{config.label}s
								</button>
							))}
						</div>
					</div>
					<EditableField
						value={title}
						onChange={setTitle}
						placeholder={`${label} section title`}
						className="mb-12 text-center"
						maxCharacters={landingPageCopyCharacterLimits.title}
						inputClassName="text-center font-bold text-3xl text-gray-900 tracking-tight sm:text-4xl"
					/>
					<ProductLinkTargetField value={linkTarget} onChange={setLinkTarget} />
					<div className="mb-8 rounded-2xl border border-indigo-100 bg-indigo-50/60 p-5">
						<div className="flex flex-wrap items-start justify-between gap-4">
							<div>
								<h3 className="text-sm font-semibold text-gray-900">Products to display</h3>
								<p className="mt-1 text-sm text-gray-600">
									Choose all {labelPlural}, or pick the individual {labelPlural} for this block.
								</p>
							</div>
							<div className="inline-flex overflow-hidden rounded-lg border border-gray-200 bg-white shadow-sm">
								<button
									type="button"
									onClick={() => setProductIds(undefined)}
									className={cn(
										"cursor-pointer px-4 py-2 text-sm font-semibold transition",
										isShowingAllProducts
											? "bg-gray-900 text-white"
											: "text-gray-700 hover:bg-gray-50",
									)}
								>
									All products
								</button>
								<button
									type="button"
									onClick={() => setProductIds(productIds ?? productOptions.map(option => option.id))}
									className={cn(
										"cursor-pointer border-l border-gray-200 px-4 py-2 text-sm font-semibold transition",
										isShowingAllProducts
											? "text-gray-700 hover:bg-gray-50"
											: "bg-gray-900 text-white",
									)}
								>
									Selected
								</button>
							</div>
						</div>
						{!isShowingAllProducts && (
							<div className="mt-5 grid gap-2 sm:grid-cols-2 lg:grid-cols-3">
								{isLoading && <p className="text-sm text-gray-500">Loading products...</p>}
								{!isLoading && productOptions.length === 0 && (
									<p className="text-sm text-gray-500">No published {labelPlural} found.</p>
								)}
								{productOptions.map(option => (
									<label
										key={option.id}
										className="flex cursor-pointer items-center gap-3 rounded-lg border border-gray-200 bg-white px-3 py-2 text-sm shadow-sm transition hover:border-gray-300"
									>
										<input
											type="checkbox"
											checked={selectedProductIds.includes(option.id)}
											onChange={() => toggleProductId(option.id)}
											className="size-4 accent-gray-900"
										/>
										<span className="line-clamp-1 font-medium text-gray-800">{option.label}</span>
									</label>
								))}
							</div>
						)}
					</div>
					<div className="grid gap-6 sm:grid-cols-2 lg:grid-cols-4">
						{Array.from({ length: 4 }, (_, i) => (
							<div
								key={i}
								className="flex flex-col overflow-hidden rounded-xl border border-gray-200 bg-white"
							>
								<div className="relative aspect-video w-full overflow-hidden bg-gray-100">
									<div className="flex size-full items-center justify-center bg-linear-to-br from-gray-200 to-gray-300">
										<Icon className="size-12 text-gray-400" />
									</div>
								</div>
								<div className="flex flex-1 flex-col p-5">
									<h3 className="text-lg font-semibold tracking-tight text-gray-900">
										{label} {i + 1}
									</h3>
									<p className="mt-2 line-clamp-2 flex-1 text-sm leading-6 text-gray-600">
										{label} description will appear here
									</p>
									<div className="mt-4 flex items-center justify-end">
										<span className="text-sm font-medium text-gray-600">Free</span>
									</div>
								</div>
							</div>
						))}
					</div>
					<p className="mt-5 text-sm text-gray-400">
						{isShowingAllProducts
							? `${label}s are loaded from your ${labelPlural} list.`
							: `${selectedProductIds.length} ${labelPlural} selected.`}{" "}
						Use the title above to customize the section heading.
					</p>
				</div>
			</section>
		</EditorShell>
	)
}

export default ProductsBlockEditor
